import {
    addDays,
    format,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
} from 'date-fns';

// A date filter that moves with the calendar. A saved dashboard used to freeze its window to
// the ISO date it was built on ("year to date" stayed 1 January of the year it was saved); a
// rolling period is stored by name and only turned into dates when the query is sent.
export const DASHBOARD_ROLLING_PERIODS = [
    'today',
    'yesterday',
    'last_7_days',
    'last_14_days',
    'last_30_days',
    'last_90_days',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
    'this_quarter',
    'last_quarter',
    'year_to_date',
    'last_12_months',
    'last_year',
] as const;
export type DashboardRollingPeriod = (typeof DASHBOARD_ROLLING_PERIODS)[number];

export const isRollingPeriod = (value: unknown): value is DashboardRollingPeriod =>
    typeof value === 'string' && (DASHBOARD_ROLLING_PERIODS as readonly string[]).includes(value);

// The period as a half-open range of whole local days: `start` is the first day in it and
// `endExclusive` the first day after it. Weeks start on Monday.
export const rollingDateRange = (
    period: DashboardRollingPeriod,
    now: Date = new Date(),
): { start: Date; endExclusive: Date } => {
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    const week = startOfWeek(today, { weekStartsOn: 1 });
    switch (period) {
        case 'today':
            return { start: today, endExclusive: tomorrow };
        case 'yesterday':
            return { start: subDays(today, 1), endExclusive: today };
        case 'last_7_days':
            return { start: subDays(today, 6), endExclusive: tomorrow };
        case 'last_14_days':
            return { start: subDays(today, 13), endExclusive: tomorrow };
        case 'last_30_days':
            return { start: subDays(today, 29), endExclusive: tomorrow };
        case 'last_90_days':
            return { start: subDays(today, 89), endExclusive: tomorrow };
        case 'this_week':
            return { start: week, endExclusive: tomorrow };
        case 'last_week':
            return { start: subWeeks(week, 1), endExclusive: week };
        case 'this_month':
            return { start: startOfMonth(today), endExclusive: tomorrow };
        case 'last_month':
            return { start: subMonths(startOfMonth(today), 1), endExclusive: startOfMonth(today) };
        case 'this_quarter':
            return { start: startOfQuarter(today), endExclusive: tomorrow };
        case 'last_quarter':
            return {
                start: subQuarters(startOfQuarter(today), 1),
                endExclusive: startOfQuarter(today),
            };
        case 'last_12_months':
            return { start: addDays(subYears(today, 1), 1), endExclusive: tomorrow };
        case 'last_year':
            return { start: subYears(startOfYear(today), 1), endExclusive: startOfYear(today) };
        case 'year_to_date':
        default:
            return { start: startOfYear(today), endExclusive: tomorrow };
    }
};

// Sent as bare dates and compared as `>= start AND < next day`. Both kinds of column take that
// correctly: a varchar date ('2026-10-30' or '2026-10-30 14:00') compares as text, where a full
// ISO timestamp would sort a plain '2026-10-30' before its own midnight and drop the first day;
// a timestamp column has the value cast to midnight by the API.
export const rollingQueryFilters = <F extends string>(
    field: F,
    period: DashboardRollingPeriod,
    now: Date = new Date(),
): { field: F; operator: 'gte' | 'lt'; value: string }[] => {
    const { start, endExclusive } = rollingDateRange(period, now);
    return [
        { field, operator: 'gte', value: format(start, 'yyyy-MM-dd') },
        { field, operator: 'lt', value: format(endExclusive, 'yyyy-MM-dd') },
    ];
};
